/* @flow */

import { v4 as uuidv4 } from 'uuid';
import { isObject, isFunction } from './utils/coreutils';
import { version as _apiVersion, name as _apiName } from '../../package';

/**
 * @description module for utility functions
 * @module utils
 */

interface WindowCrypto {
  getRandomValues(Uint8Array): Uint8Array;
}

/**
 * returns JSAPI library name
 *
 * @exports apiName
 * @returns {string} - JSAPI Name
 */
export function apiName(): string {
  return _apiName;
}

/**
 *
 * JSAPI library version
 *
 * @exports apiVersion
 * @returns {string} - JSAPI Versions string
 */
export function apiVersion(): string {
  return _apiVersion;
}

/**
 * random string generator
 * @param {number} Length of the random string
 * @returns {string} - Random number
 */
export function getRandom(length: number = 9): string {
  const start = 2;
  const end = start + length;
  return Math.random().toString().slice(start, end);
}

/**
 * Returns random data using the Crypto API if available and Math.random if not
 * Method is from https://gist.github.com/jed/982883 like generateUUID, direct link https://gist.github.com/jed/982883#gistcomment-45104
 *
 * @returns {number} - unique number
 * @access private
 */
function _getRandomData(): number {
  return Math.random() * 16;
}

/**
 * Returns a random v4 UUID of the form xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx,
 * where each x is replaced with a random hexadecimal digit from 0 to f,
 * and y is replaced with a random hexadecimal digit from 8 to b.
 * https://gist.github.com/jed/982883 via node-uuid
 *
 * @param {string} match - placeholder
 * @returns {string} - unique user id
 */
function _generateUUIDOld(match?: string = ''): string {
  if (match.length === 1) {
    const nMatch = Number(match);
    // eslint-disable-next-line no-bitwise
    return (nMatch ^ (_getRandomData() >> (nMatch / 4))).toString(16);
  }
  // $FlowFixMe[prop-missing]
  return `${1e7}-${1e3}-${4e3}-${8e3}-${1e11}`.replaceAll(
    /[018]/g,
    _generateUUIDOld
  );
}

export function generateUUID(): string {
  // uuidv4 works when the environment supports Crypto.getRandomValues.
  // Fallback to the old method for old browsers
  const objCrypto: WindowCrypto = window.crypto;
  // $FlowFixMe[incompatible-use]
  if (isObject(objCrypto) && isFunction(objCrypto.getRandomValues)) {
    return uuidv4();
  }
  // fall back to the old method of generating UUID by hand
  return _generateUUIDOld();
}
/**
 *Get top level domain (netzero.net or juno.com)
 *
 * @param {window} w - window object
 * @returns {string} - Top level domain
 */
export function getTopLevelDomain(w?: WindowProxy = window): string {
  // Split domain by '.' , get last two elements in the array and join them
  const loc: Location = w.location;
  return loc.host.split('.').slice(-2).join('.');
}
/**
 *Get current domain
 *
 * @param {window} w - window object
 * @returns {string} - Top level domain
 */
export function getDomain(w?: WindowProxy = window): string {
  // Split domain by '.' , get last two elements in the array and join them
  const loc: Location = w.location;
  return loc.host;
}
